import mapKeys from 'lodash/mapKeys';
import snakeCase from 'lodash/snakeCase';
import {
  takeLeading,
  call,
  all,
  select,
  put,
  takeEvery,
} from 'redux-saga/effects';
import {ActionType, actions} from './action';
import {
  getApiAuthResult,
  I_ApiAuthResult,
  getUser,
  I_User,
  I_LastLoginAuth,
  getLastLoginAuth,
  getTermsList,
  I_TermsItem,
} from './apiGet';
import {
  AUTH_HOST,
  DOMAIN_MAP,
  ENV_NAME,
  REGION,
  WEB_HOST,
} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {pToast} from '../../common/container/PToast';
import {
  getDeviceId,
  getDeviceName,
  getLoginReturnPageInfo,
} from '../../common/function';
import {actions as commonActions} from '../../common/state/action';
import {IReduxState} from '../../common/store';
import {IUserAgent} from '../../common/type';
import callApi, {callApiList, I_ApiResult} from '../../common/util/api';
import {processAuthBlockedUser} from '../../common/util/api/apiError';
import {pushLog} from '../../common/util/debug';
import {makeFetchSaga} from '../../common/util/fetch';
import {getUrl, routeTo} from '../../common/util/PageRouter';
import {typedSelect} from '../../common/util/saga';
import {
  deleteLocalStorage,
  getLocalStorageString,
} from '../../common/util/storage';
import {sendGtmEvent} from '../../common/util/tracking/gtm';
import {callApiUser, clearUserInfo} from '../../commonSub/function';
import {
  API_AUTH,
  AUTH_RESULT_PARAMS_LS_KEY,
  deleteAccessTokenAndUser,
  getAccessToken,
  getStoredApiParamsAuthResult,
  RETURN_PAGE_INFO_LS_KEY,
  setAccessToken,
  showPAlertDifferentRegion,
  showPAlertTermsNotAgreed,
  startIdVerification,
} from '../common';
import {ClientType, IUser, NextAuthStep, UserStatus} from '../type';

function parseAndGetAccessToken(data: I_ApiAuthResult) {
  if (data && data.accessToken) {
    return data.accessToken;
  } else {
    // 전화번호 뺏긴 후 다시 로그인 시도할때는 전화번호 인증 과정을 거치기 때문에 data에 accessToken이 없다.
    return getAccessToken();
  }
}

function* fetchFinish(action: ReturnType<typeof actions.fetchFinish>) {
  const {needReload} = action.payload;
  const apiParamsAuthResult = yield* typedSelect(
    (state: IReduxState) => state.auth.apiParamsAuthResult
  );
  if (apiParamsAuthResult) {
    const {success, data} = yield call(callApi, {
      apiHost: AUTH_HOST,
      url: `${API_AUTH}/auth/finish`,
      params: {...mapKeys(apiParamsAuthResult, (_, k) => snakeCase(k))},
    });
    if (success && data) {
      const apiParamsAuthResult = getApiAuthResult(data);
      pushLog({msg: 'apiParamsAuthResult', params: apiParamsAuthResult});
      yield put(actions.setValue('apiParamsAuthResult', apiParamsAuthResult));
      const accessToken = parseAndGetAccessToken(data);
      const user = getUser(data.userResponse, accessToken);
      if (apiParamsAuthResult.sessionRegion !== user.region) {
        const {okClicked} = yield call(showPAlertDifferentRegion);
        // TODO: 특정 region의 특정 언어로 보여야 한다면, language를 쿠키에 넣어주자. (다국어 리전이 생겼을 때 처리)
        location.assign(
          okClicked
            ? DOMAIN_MAP[user.region][ENV_NAME]
            : WEB_HOST + getLoginReturnPageInfo()
        );
        return;
      }
      if (
        accessToken &&
        data.userResponse &&
        // NextAuthStep.NeedEvictionConfirmation 경우 useAuthResult에서 다시 fetchfinish 액션 호출
        apiParamsAuthResult.authStatus !== NextAuthStep.NeedEvictionConfirmation
      ) {
        const user = getUser(data.userResponse, accessToken);
        setAccessToken(accessToken);
        yield put(actions.setValue('user', user));
        yield call(processUserStatus, {user, needReload, isLogin: true});
        if (apiParamsAuthResult.isSignUp) {
          yield call(sendGtmEvent, 'COMPLETE_REGISTRATION');
        }
        yield call(clearStoredAuthData);
      }
    } else {
      yield call(clearStoredAuthData);
    }
  }
}

function* fetchUser(action: ReturnType<typeof actions.fetchUser>) {
  const {needDetail} = action.payload;
  const accessToken = getAccessToken();
  if (accessToken) {
    const {success, data}: I_ApiResult<I_User> = yield call(
      callApiUser,
      needDetail
    );

    if (success && data) {
      const user = getUser(data, accessToken);
      if (user.region !== REGION) {
        const {okClicked} = yield call(showPAlertDifferentRegion);
        okClicked && location.assign(DOMAIN_MAP[user.region][ENV_NAME]);
        !okClicked && deleteAccessTokenAndUser();
      } else {
        yield call(processUserStatus, {user});
        yield put(actions.setValue('user', user));
      }
    }
  }

  yield put(actions.setValue('isFetchUserFinished', true));
}

function* processUserStatus({
  user,
  needReload = false,
  isLogin,
}: {
  user: IUser;
  needReload?: boolean;
  isLogin?: boolean;
}) {
  const {userStatus, blockedAt} = user;
  switch (userStatus) {
    case UserStatus.BlockedByManager:
      if (blockedAt) {
        yield call(processAuthBlockedUser, blockedAt);
      }
      break;
    case UserStatus.RequiredTermNotAgreed:
      if (!isLogin) {
        yield call(showPAlertTermsNotAgreed);
      }
      break;
    case UserStatus.Ok:
      if (needReload) {
        const returnPageInfo = getLocalStorageString({
          key: RETURN_PAGE_INFO_LS_KEY,
        });
        if (returnPageInfo) {
          location.assign(returnPageInfo);
        } else {
          location.reload();
        }
      }
      break;
    default:
      break;
  }
}

function* fetchLogout() {
  yield put(commonActions.setValue('isOnLogout', true));
  const user: IUser | undefined = yield select(
    (state: IReduxState) => state.auth.user
  );
  if (!user) {
    return;
  }
  const {success, data} = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/user/logout`,
    params: {access_token: user.accessToken},
  });
  if (success && data) {
    clearUserInfo();
  }
}

function* fetchChangeNickname(
  action: ReturnType<typeof actions.fetchChangeNickname>
) {
  const user = yield* typedSelect(state => state.auth.user);

  if (!user) {
    return;
  }

  const {nickname} = action.payload;
  const {success} = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/user/nick`,
    method: 'post',
    params: {user_nick: nickname, access_token: user.accessToken},
    processApiErrorCallback: error => {
      if (error.errorType === 'BANNED_NICKNAME') {
        pToast(t => t('more_myinfo_nickname_banned_toast', {word: nickname}));
        return true;
      } else {
        return false;
      }
    },
  });

  if (success) {
    const newUser = {...user, nickname};
    yield put(actions.setValue('user', newUser));
    pToast(t => t('more_myinfo_nickname_toast'));
  }
}

function* fetchRefreshToken() {
  const userAgent: IUserAgent | undefined = yield select(
    ({common}: IReduxState) => common.userAgent
  );
  const {success, data}: I_ApiResult<string> = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/token/refresh`,
    params: {
      access_token: getAccessToken(),
      device_id: getDeviceId(),
      device_name: getDeviceName(userAgent),
      os: userAgent?.osName,
    },
  });
  if (success && data) {
    const newToken = data;
    setAccessToken(newToken);
    yield put(actions.setPropertyValues('user', {accessToken: newToken}));
  }
}

function* fetchLastLoginAuth() {
  const {success, data}: I_ApiResult<I_LastLoginAuth> = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/user/last_login`,
    params: {device_id: getDeviceId()},
  });
  if (success && data) {
    yield put(actions.setValue('lastLoginAuth', getLastLoginAuth(data)));
  }
}

function* fetchTermsList(action: ReturnType<typeof actions.fetchTermsList>) {
  const {sessionId, isParent} = action.payload;
  const {success, data}: I_ApiResult<I_TermsItem[]> = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: isParent
      ? `${API_AUTH}/auth/terms/guardian/latest`
      : `${API_AUTH}/auth/terms/latest`,
    params: {
      [isParent ? 'verification_session_id' : 'session_id']: sessionId,
      access_token: isParent ? getAccessToken() : undefined,
    },
  });
  if (success && data) {
    yield put(
      actions.setValue(
        isParent ? 'termsParentList' : 'termsList',
        getTermsList(data)
      )
    );
  }
}

function* fetchAcceptTerms(
  action: ReturnType<typeof actions.fetchAcceptTerms>
) {
  const {accepted, birthDate, version, isParent, verificationSessionId} =
    action.payload;
  const apiParamsAuthResult = yield* typedSelect(getStoredApiParamsAuthResult);
  const {success, data}: I_ApiResult<I_ApiAuthResult> = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: isParent
      ? `${API_AUTH}/auth/terms/guardian/accept`
      : `${API_AUTH}/auth/terms/accept`,
    method: 'post',
    params: {
      ...mapKeys(apiParamsAuthResult, (_, k) => snakeCase(k)),
      accepted,
      version,
      birth_date: birthDate,
      verification_session_id: verificationSessionId,
    },
  });
  if (success) {
    if (isParent) {
      const returnPageInfo = getLoginReturnPageInfo();
      startIdVerification({
        sessionId: apiParamsAuthResult.sessionId,
        accessToken: getAccessToken(),
        verificationSessionId,
        isParentVerification: true,
        redirectUrl: returnPageInfo && `${WEB_HOST}${returnPageInfo}`,
      });
      deleteLocalStorage({key: RETURN_PAGE_INFO_LS_KEY});
    } else if (data) {
      const nextApiParamsAuthResult = getApiAuthResult(data, true);
      yield put(
        actions.setValue('apiParamsAuthResult', nextApiParamsAuthResult)
      );
    }
  }
}

function* clearStoredAuthData() {
  deleteLocalStorage({key: RETURN_PAGE_INFO_LS_KEY});
  deleteLocalStorage({key: AUTH_RESULT_PARAMS_LS_KEY});
  yield put(actions.clearAuthParams());
}

function* fetchKakaoOAuthLogin(
  action: ReturnType<typeof actions.fetchKakaoOAuthLogin>
) {
  const {code, userAgent} = action.payload;
  const {success, data}: I_ApiResult<I_ApiAuthResult> = yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/oauth/app`,
    method: 'post',
    params: {
      region: REGION,
      locale: 'ko_KR',
      client_type: ClientType.Mobile,
      session_type: 'AW',
      login_method: 'KA',
      user_agent: encodeURIComponent(userAgent.rawData),
      os: userAgent?.osName,
      authorization_code: code,
      device_id: getDeviceId(),
      device_name: getDeviceName(userAgent),
      // 여기서 넣어준 redirect_uri는 서버에서 oauth token을 받아오기 위해 필요한 값. 실제 redirection이 발생하지 않는다.
      redirect_uri: `${WEB_HOST}${getUrl({page: Page.AuthOAuth})}`,
    },
    processApiErrorCallback: error => {
      if (error.errorType === 'DUPLICATE_DATA') {
        routeTo({page: Page.Home}, {isReplace: true});
      }
      return false;
    },
  });

  if (success && data) {
    const apiParamsAuthResult = getApiAuthResult(data, true);
    yield put(actions.setValue('apiParamsAuthResult', apiParamsAuthResult));
  }
}

function* fetchDeleteUnverifiedUserData(
  action: ReturnType<typeof actions.fetchDeleteUnverifiedUserData>
) {
  const verificationSessionId = action.payload.verificationSessionId;
  yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/auth/signup/id/verify/data`,
    method: 'delete',
    params: {
      verification_session_id: verificationSessionId,
    },
  });
}

function* fetchDeleteSnsUserData(
  action: ReturnType<typeof actions.fetchDeleteSnsUserData>
) {
  const sessionId = action.payload.sessionId;
  yield call(callApi, {
    apiHost: AUTH_HOST,
    url: `${API_AUTH}/oauth/signup/data`,
    method: 'delete',
    params: {session_id: sessionId},
  });
}

function* fetchUserCherryPicking() {
  const {success, data} = yield call(callApiList, [
    {
      apiHost: AUTH_HOST,
      url: `${API_AUTH}/auth/device_id/users`,
      params: {
        device_id: getDeviceId(),
      },
    },
    {
      url: '/gift/v1/check-join-quest-exist',
    },
  ]);

  if (success && data) {
    const isRegisteredDeveiceId = !!(data[0]?.length > 0);
    const isOnQuestCampaign = !!data[1]?.joinQuestExistFlag;

    yield put(
      actions.setValue(
        'isCherryPicking',
        isRegisteredDeveiceId || !isOnQuestCampaign
      )
    );
  }
}

export default function* saga() {
  yield all([
    takeLeading(
      ActionType.FetchFinish,
      makeFetchSaga({fetchSaga: fetchFinish, canCache: false})
    ),
    takeEvery(
      ActionType.FetchUser,
      makeFetchSaga({
        fetchSaga: fetchUser,
        canCache: false,
        isIgnoreError: true,
      })
    ),
    takeLeading(
      ActionType.FetchLogout,
      makeFetchSaga({fetchSaga: fetchLogout, canCache: false})
    ),
    takeLeading(
      ActionType.FetchChangeNickname,
      makeFetchSaga({fetchSaga: fetchChangeNickname, canCache: false})
    ),
    takeLeading(
      ActionType.FetchRefreshToken,
      makeFetchSaga({fetchSaga: fetchRefreshToken, canCache: false})
    ),
    takeLeading(
      ActionType.FetchLastLoginAuth,
      makeFetchSaga({fetchSaga: fetchLastLoginAuth, canCache: false})
    ),
    takeLeading(
      ActionType.FetchTermsList,
      makeFetchSaga({fetchSaga: fetchTermsList, canCache: true})
    ),
    takeLeading(
      ActionType.FetchParentTermsList,
      makeFetchSaga({fetchSaga: fetchTermsList, canCache: true})
    ),
    takeLeading(
      ActionType.FetchAcceptTerms,
      makeFetchSaga({fetchSaga: fetchAcceptTerms, canCache: false})
    ),
    takeLeading(
      ActionType.FetchKakaoOAuthLogin,
      makeFetchSaga({fetchSaga: fetchKakaoOAuthLogin, canCache: false})
    ),
    takeLeading(
      ActionType.FetchDeleteUnverifiedUserData,
      makeFetchSaga({
        fetchSaga: fetchDeleteUnverifiedUserData,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchDeleteSnsUserData,
      makeFetchSaga({
        fetchSaga: fetchDeleteSnsUserData,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchUserCherryPicking,
      makeFetchSaga({
        fetchSaga: fetchUserCherryPicking,
        canCache: false,
      })
    ),
  ]);
}
